import echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import { BAR, FUNNEL, HORIZONTAL_STACKED, LINE, PIE, PIE_GRADE } from '../Charts/types';
import { chartOptions } from './models/chartOptions';
import { getChartConfig, isChartWithAllOptions } from './models/echartOptions';
import * as Ui from './styles';

export default function ChartPreview({
  theme,
  onUpdateCheckedPreviewOptions,
  selectedChartType,
}) {
  /**
   * Opções que serão enviadas no evento onExport ao selecionar as opções no componente de preview
   * as opções são { legend, statement, percentage, gridLines }, as opções visíveis dependem do tipo
   * de gráfico selecionado, por exemplo, o gráfico de barras possui a opção gridLines, já o gráfico
   * de pizza não possui.
   *
   * As opções setadas de maneira default são "legend" e "statement" (legenda e enunciado), pois são
   * opções contidas em todos os tipos de gráficos.
   */
  const [checkedOptions, setCheckedOptions] = useState({
    legend: true,
    statement: true,
  });

  const chartTypes = {
    bar: BAR,
    line: LINE,
    funnel: FUNNEL,
    pie: PIE,
    pieGrade: PIE_GRADE,
    stacked: HORIZONTAL_STACKED,
  };

  const chartTypesKeys = Object.keys(chartTypes);

  // useEffect: Seta as opções visíveis de acordo com o tipo do gráfico exportado.
  useEffect(() => {
    if (isChartWithAllOptions(selectedChartType)) {
      setCheckedOptions({
        ...checkedOptions,
        percentage: true,
        gridLines: true,
      });
    } else if (chartTypesKeys.includes(selectedChartType)) {
      setCheckedOptions({
        ...checkedOptions,
        percentage: true,
      });
    }
  }, [selectedChartType]);

  /**
   * useEffect para atualizar as opções selecionadas à cada ação do usuário, o estado atual de opções
   * selecionadas serão enviadas para o componente pai via callback.
   */
  useEffect(() => {
    onUpdateCheckedPreviewOptions(checkedOptions);
  }, [checkedOptions]);

  const chartContainerRef = useRef(null);

  // Utilizada como filtro para as opções (checkboxes) que serão renderizadas no componente de preview.
  function getFilteredOptionsByChartType(option) {
    return checkedOptions.hasOwnProperty(option.value);
  }

  // Função de supressão de da interação da legenda no gráfico de preview
  function suppressSelection(chart, params) {
    chart.dispatchAction({
      type: 'legendSelect',
      name: params.name,
    });
  }

  // CORE: Inicialização da instancia do echarts para o gráfico generico
  useEffect(() => {
    if (chartContainerRef && chartContainerRef.current) {
      const myChart = echarts.init(chartContainerRef.current);
      const chartConfig = getChartConfig(selectedChartType, theme, checkedOptions);

      myChart.setOption({ animation: false, ...chartConfig });

      // Evendo de seleção da da legenda
      myChart.on('legendselectchanged', (params) => {
        suppressSelection(myChart, params);
      });
    }
  }, [chartContainerRef, theme, checkedOptions, selectedChartType]);

  function checkOption(option, event) {
    const newState = { ...checkedOptions, [option.value]: event.target.checked };
    setCheckedOptions(newState);
  }

  function renderOptions() {
    return (
      <Ui.OptionsWrapper>
        {chartOptions.filter(getFilteredOptionsByChartType).map((option, index) => (
          <Ui.OptionContainer key={`key#${index}`}>
            <Ui.Option
              type="checkbox"
              onChange={(event) => {
                checkOption(option, event);
              }}
              defaultChecked={!!true}
            />
            <Ui.OptionLabel>{option.label}</Ui.OptionLabel>
          </Ui.OptionContainer>
        ))}
      </Ui.OptionsWrapper>
    );
  }

  return (
    <Ui.Wrapper>
      <Ui.Label>Partes do gráfico</Ui.Label>
      {renderOptions()}
      <Ui.PreviewCardContainer>
        <Ui.PreviewHeader>
          <Ui.Label>PREVIEW GRÁFICO</Ui.Label>
        </Ui.PreviewHeader>
      </Ui.PreviewCardContainer>
      <Ui.PreviewWrapper>
        <Ui.PreviewTitleContainer>
          { checkedOptions.statement && (
            <Ui.Label>
              <b>Tipo de pergunta </b>
              - Enunciado da pergunta
            </Ui.Label>
          )}
        </Ui.PreviewTitleContainer>
        <Ui.ChartContainer ref={chartContainerRef}>
          CHART
        </Ui.ChartContainer>
      </Ui.PreviewWrapper>
    </Ui.Wrapper>
  );
}
